/**
 * Error Boundary Component
 * Catches JavaScript errors in child components and displays fallback UI
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { WarningIcon, RefreshIcon } from '@/components/shared/Icons';

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error for debugging
    console.error('Error Boundary caught an error:', error, errorInfo);
    
    this.setState({ errorInfo });

    // Here you could send the error to an error reporting service
    // Example: logErrorToService(error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex items-center justify-center min-h-screen p-4 bg-background">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-destructive/10 flex items-center justify-center">
              <WarningIcon size={32} className="text-destructive" />
            </div>
            
            <h2 className="text-2xl font-bold mb-2 text-foreground">
              Algo deu errado
            </h2>
            
            <p className="text-muted-foreground mb-6">
              Ocorreu um erro inesperado. Por favor, tente recarregar a página.
            </p>

            {/* Show error details in development */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mb-6 text-left p-4 bg-muted rounded-lg">
                <summary className="cursor-pointer font-medium text-sm mb-2">
                  Detalhes do erro (desenvolvimento)
                </summary>
                <pre className="text-xs overflow-auto whitespace-pre-wrap text-destructive">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            <div className="flex gap-3 justify-center">
              <Button
                variant="outline"
                onClick={this.handleReset}
                className="gap-2"
              >
                Tentar novamente
              </Button>
              
              <Button
                onClick={this.handleReload}
                className="gap-2"
              >
                <RefreshIcon size={16} />
                Recarregar página
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * withErrorBoundary HOC
 * Wraps a component with ErrorBoundary for easier usage
 */
export function withErrorBoundary(Component, fallback = null) {
  return function WithErrorBoundary(props) {
    return (
      <ErrorBoundary fallback={fallback}>
        <Component {...props} />
      </ErrorBoundary>
    );
  };
}

export default ErrorBoundary;
